import { useEffect, useRef, useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaCheckCircle, FaTimesCircle, FaSpinner, FaHourglassHalf } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import "../styles/receipt.css";

const ReceiptPage = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { buyAirtime, buySmartData, fetchNotifications, buyCableTV, sendBulkSms, palmPayPayout, purchaseEducationToken, generateRechargeCardPin, payElectricityBill } = useContext(AuthContext);

    const [status, setStatus] = useState("processing"); // 'processing' | 'success' | 'error'
    const [errorMessage, setErrorMessage] = useState("");
    const hasRunRef = useRef(false); // Prevent duplicate requests

    useEffect(() => {
        if (!state || !state.type || !state.details || hasRunRef.current){
            navigate("/dashboard", { replace: true });
            return;
        } 
        hasRunRef.current = true;

        const processTransaction = async () => {
            const { type, details } = state;

            try {
                let result;

                switch (type) {
                    case "airtime":
                        result = await buyAirtime(
                            details.network?.toLowerCase(),
                            details.amount,
                            details.phoneNumber,
                        );
                        break;
                    case "data":
                        result = await buySmartData(
                            details.network?.toUpperCase(),
                            details.planCode,
                            details.planSize,
                            details.phoneNumber,
                            parseFloat(details.amount) // ensure it's a number
                        );
                        break;
                    case "cabletv": // updated key
                        result = await buyCableTV(
                            details.smartcardNumber,
                            details.phoneNumber,
                            details.variationId,
                            details.serviceId,
                            details.amount
                        );
                        break;

                    case "bulksms": // ✅ Handle Bulk SMS
                        result = await sendBulkSms({
                            message: details.message,
                            sendto: details.sendto,
                            sender: details.sender,
                            totalCost: details.totalCost
                        });
                        break;

                    case "recharge-card":
                        result = await generateRechargeCardPin(
                            details.network?.toUpperCase(),
                            parseInt(details.amount.replace("₦", "")),
                            parseInt(details.quantity),
                            details.totalCost
                        );
                        break;

                    case "electricity":
                        result = await payElectricityBill(
                            details.service,
                            details.meterNumber,
                            details.meterType,
                            details.accessToken,
                            parseFloat(details.amount)
                        );
                        break;

                    case "education":
                        result = await purchaseEducationToken(
                            details.eduCode,
                            details.price
                        );
                        break;

                    case "withdraw":
                        result = await palmPayPayout({
                            payeeName: details.payeeName,
                            payeeBankCode: details.payeeBankCode,
                            payeeBankAccNo: details.payeeBankAccNo,
                            payeePhoneNo: details.payeePhoneNo || "", // optional
                            amount: details.amount,
                            remark: details.remark || `Withdrawal by user`,
                        });
                        break;


                    default:
                        throw new Error("Unsupported transaction type.");
                }

                if (result?.pending) {
                    setStatus("pending");
                    await fetchNotifications?.();
                }
                else if (result?.error) {
                    setErrorMessage(result.error || "Transaction failed.");
                    setStatus("error");
                } else {
                    setStatus("success");
                    await fetchNotifications?.();
                }
            } catch (err) {
                console.error("Transaction error:", err);
                setErrorMessage("Server error. Please try again later.");
                setStatus("error");
            }
        };

        processTransaction();
    }, [state]);

    const handleRetry = () => {
        if (status === "processing") return;
        navigate(-1); // Back to PIN modal
    };

    const handleClose = () => {
        if (state?.type === "recharge-card") {
            navigate("/generated-pins", { replace: true });
        }
        else if (state?.type === "electricity") {
            navigate("/electricity-details", { replace: true });
        } else if (state?.type === "education") {
            navigate("/result-details", { replace: true });
        } else {
            navigate("/dashboard", { replace: true });
        }
    };


    return (
        <div className="receipt-container">
            {status === "processing" && (
                <>
                    <FaSpinner className="receipt-icon processing" />
                    <p className="receipt-message">Processing your payment...</p>
                </>
            )}

            {status === "success" && (
                <>
                    <FaCheckCircle className="receipt-icon success" />
                    <p className="receipt-message">Transaction Successful </p>
                    <button onClick={handleClose} className="receipt-button success">
                        Close
                    </button>
                </>
            )}

            {status === "pending" && (
                <>
                    <FaHourglassHalf className="receipt-icon pending" />
                    <p className="receipt-message">Your transaction is being processed...</p>
                    <button onClick={handleClose} className="receipt-button pending">
                        Close
                    </button>
                </>
            )}


            {status === "error" && (
                <>
                    <FaTimesCircle className="receipt-icon error" />
                    <p className="receipt-message">{errorMessage || "Transaction Failed ❌"}</p>
                    <button onClick={handleRetry} className="receipt-button error">
                        Retry
                    </button>
                </>
            )}
        </div>
    );
};

export default ReceiptPage;
